/**
 * PermissionsContext
 *
 * Fetches the role-permissions matrix from the backend on load.
 * Provides `canAccess(page, role)` for route guards and sidebar filtering,
 * and `updatePermissions(matrix)` for the super-admin edit UI.
 *
 * Falls back to hardcoded defaults if the backend is unreachable, so the
 * app is never blocked waiting for this.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { canManageOffers } from '../lib/canManageOffers';

// Hardcoded defaults — used as fallback when the backend hasn't responded yet.
// The `offers` row is fully locked: base access (super_admin, entity_manager) is
// by role; worker access (agent, dept_user, dept_manager) is via the
// can_manage_offers capability flag on the user row.
export const DEFAULT_PERMISSIONS = {
  dashboard:  { label: 'Dashboard',       allowedRoles: ['super_admin','entity_manager','agent','dept_user','dept_manager'], lockedRoles: ['super_admin'] },
  tickets:    { label: 'Tickets',         allowedRoles: ['super_admin','entity_manager','agent','dept_user','dept_manager'], lockedRoles: ['super_admin'] },
  pending_action: { label: 'Pending Action', allowedRoles: ['super_admin','entity_manager','agent','dept_user','dept_manager'], lockedRoles: ['super_admin'] },
  new_ticket: { label: 'New Ticket',      allowedRoles: ['super_admin','entity_manager','agent','dept_user','dept_manager'], lockedRoles: ['super_admin'] },
  users:      { label: 'User Management', allowedRoles: ['super_admin','entity_manager'],                                    lockedRoles: ['super_admin'] },
  admin:      { label: 'Admin Panel',     allowedRoles: ['super_admin'],                                                     lockedRoles: ['super_admin','entity_manager','agent','dept_user','dept_manager'] },
  offers:     { label: 'Offers & Promos', allowedRoles: ['super_admin','entity_manager'],                                    lockedRoles: ['super_admin','entity_manager','agent','dept_user','dept_manager'] },
  flows:      { label: 'Flows',           allowedRoles: ['super_admin'],                                                     lockedRoles: ['super_admin'] },
  sample_collections: { label: 'Sample Collections', allowedRoles: ['super_admin','entity_manager','dept_manager','dept_user'], lockedRoles: ['super_admin'] },
  email_monitor:      { label: 'Email Monitor',       allowedRoles: ['super_admin','entity_manager'],                          lockedRoles: ['super_admin','entity_manager','agent','dept_user','dept_manager'] },
};

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loadingPerms, setLoadingPerms] = useState(true);

  const fetchPermissions = useCallback(() => {
    // Only fetch if the user is logged in (token present)
    if (!localStorage.getItem('token')) {
      setLoadingPerms(false);
      return;
    }
    api.get('/role-permissions')
      .then(r => {
        if (r.data && Object.keys(r.data).length) {
          // Merge with defaults so any missing keys are covered
          setPermissions({ ...DEFAULT_PERMISSIONS, ...r.data });
        }
      })
      .catch(() => { /* keep defaults */ })
      .finally(() => setLoadingPerms(false));
  }, []);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  /** Returns true if `user` can access `page`. super_admin always can.
   *  For 'offers' specifically, the capability flag (can_manage_offers)
   *  grants access to any role. All other pages remain role-driven.  */
  const canAccess = useCallback((page, user) => {
    if (!user?.role) return false;
    if (user.role === 'super_admin') return true;
    if (page === 'offers' && canManageOffers(user)) return true;
    return (permissions[page]?.allowedRoles || []).includes(user.role);
  }, [permissions]);

  /**
   * Save an updated matrix to the backend.
   * `draft` shape: { page: roleArray[] } — same as the PUT body.
   */
  const updatePermissions = useCallback(async (draft) => {
    const r = await api.put('/role-permissions', draft);
    // Backend returns the full updated matrix
    if (r.data && Object.keys(r.data).length) {
      setPermissions({ ...DEFAULT_PERMISSIONS, ...r.data });
    }
  }, []);

  /** Reset to system defaults. */
  const resetPermissions = useCallback(async () => {
    const r = await api.post('/role-permissions/reset');
    if (r.data && Object.keys(r.data).length) {
      setPermissions({ ...DEFAULT_PERMISSIONS, ...r.data });
    }
  }, []);

  return (
    <PermissionsContext.Provider value={{
      permissions,
      loadingPerms,
      canAccess,
      updatePermissions,
      resetPermissions,
      refreshPermissions: fetchPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider');
  return ctx;
};
